<script setup>
import { computed, ref } from 'vue';

// One-time code input (shadcn InputOTP): one box per digit, typing moves
// forward, Backspace moves back, and pasting a whole code fills every box.
const props = defineProps({
  length: { type: Number, default: 6 },
  disabled: { type: Boolean, default: false },
});
const code = defineModel({ type: String, default: '' });
const emit = defineEmits(['complete']);

const boxes = ref([]);
const digits = computed(() => Array.from({ length: props.length }, (_, i) => code.value[i] ?? ''));

function setCode(value) {
  code.value = value.replace(/\D/g, '').slice(0, props.length);
  if (code.value.length === props.length) emit('complete', code.value);
}

function focus(index) {
  boxes.value[Math.max(0, Math.min(props.length - 1, index))]?.focus();
}

function onInput(event, index) {
  const typed = event.target.value.replace(/\D/g, '');
  const chars = digits.value.slice();
  chars[index] = typed.slice(-1);
  setCode(chars.join(''));
  event.target.value = chars[index];
  if (typed) focus(index + 1);
}

function onKeydown(event, index) {
  if (event.key === 'Backspace' && !digits.value[index]) focus(index - 1);
  else if (event.key === 'ArrowLeft') focus(index - 1);
  else if (event.key === 'ArrowRight') focus(index + 1);
}

function onPaste(event) {
  event.preventDefault();
  setCode(event.clipboardData.getData('text'));
  focus(code.value.length);
}
</script>

<template>
  <div class="ui-otp" role="group" aria-label="One-time code">
    <input
      v-for="(digit, i) in digits"
      :key="i"
      :ref="(el) => (boxes[i] = el)"
      :value="digit"
      class="ui-otp__box"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="1"
      :disabled="disabled"
      :aria-label="`Digit ${i + 1}`"
      @input="onInput($event, i)"
      @keydown="onKeydown($event, i)"
      @paste="onPaste"
      @focus="$event.target.select()"
    />
  </div>
</template>

<style scoped>
.ui-otp {
  display: flex;
  justify-content: center;
  gap: 8px;
}
.ui-otp__box {
  width: 44px;
  height: 48px;
  border: 1px solid var(--input);
  border-radius: 8px;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  font-size: 20px;
  font-weight: 500;
  text-align: center;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ui-otp__box:focus {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.2);
}
.ui-otp__box:disabled {
  opacity: 0.5;
}
@media (max-width: 360px) {
  .ui-otp__box {
    width: 38px;
    height: 44px;
  }
}
</style>
